// Listening-history model, ported from lib/domain/entities/book_history_event.dart
// and book_history_timeline.dart.

export const HISTORY_EVENT_TYPES = [
  'playbackStarted',
  'playbackResumed',
  'playbackPaused',
  'playbackStopped',
  'seek',
  'jumpBackward',
  'jumpForward',
  'chapterSelected',
  'chapterNext',
  'chapterPrevious',
  'bookmarkCreated',
  'bookmarkRemoved',
  'progressReset',
  'markedFinished',
  'markedUnfinished',
  'downloadStarted',
  'downloadCompleted',
  'downloadRemoved',
  'downloadCancelled',
  'playbackSpeedChanged',
  'sleepTimerSet',
  'sleepTimerCancelled',
  'sleepTimerExpired',
  'bookAction',
] as const;

/** Wire value = the Flutter enum name. */
export type BookHistoryEventType = (typeof HISTORY_EVENT_TYPES)[number];

export type HistoryEventSource = 'player_provider' | 'bookmark_provider' | 'library_provider' | 'download_provider' | 'app';

/** All fields optional; serialised without empty values. `chapterIndex` is 0-based. */
export interface BookHistoryPayload {
  fromPositionMs?: number;
  toPositionMs?: number;
  chapterId?: string;
  chapterTitle?: string;
  chapterIndex?: number;
  bookmarkId?: string;
  note?: string;
  playbackSpeed?: number;
  sleepTimerDurationMs?: number;
  reason?: string;
}

export interface BookHistoryEvent {
  /** uuid v4 */
  id: string;
  bookId: string;
  /** Epoch milliseconds (UTC instant). */
  timestampMs: number;
  type: BookHistoryEventType;
  payload: BookHistoryPayload;
  /** Explicit listening-session id from the player, when known. */
  sessionId?: string | null;
  source?: string | null;
  dedupeKey?: string | null;
}

export interface BookHistoryCursor {
  timestampMs: number;
  eventId: string;
}

export interface BookHistoryPage {
  events: BookHistoryEvent[];
  /** Null when there are no older events. */
  nextCursor: BookHistoryCursor | null;
}

export interface BookHistorySessionGroup {
  /** `explicit:<sessionId>` or `computed:<n>:<firstEventId>`. */
  key: string;
  sessionId: string | null;
  sessionStartMs: number;
  sessionEndMs: number;
  totalListeningMs: number;
  /** Oldest first. */
  events: BookHistoryEvent[];
  isActionOnly: boolean;
}

export interface BookHistoryDayGroup {
  /** Local midnight of the day (epoch ms). */
  localDayStartMs: number;
  /** Newest session first. */
  sessions: BookHistorySessionGroup[];
  totalEventCount: number;
}

/** Sessions split after this much inactivity (kDefaultHistorySessionIdleGap). */
export const HISTORY_SESSION_IDLE_GAP_MS = 15 * 60_000;
/** History page size (BookHistoryViewModel default). */
export const HISTORY_PAGE_SIZE = 200;
